import { computed, makeObservable } from 'mobx'
import {
  removeUserAnswer,
  setAnswerObjects,
  submitUserAnswer,
  updateAnswerObjects,
  updateAnswerObjectsOrder,
  upsertUserAnswer,
} from '../../firestore/RoomStateAnswer'
import {
  deleteRoomStateCollaborationData,
  updateRoomStateCollaborationDataOrder,
  upsertMultipleRoomStateCollaborationDatas,
  upsertRoomStateCollaborationData,
} from '../../firestore/RoomStateCollaborationData'
import { CollaborationDataType } from '../../firestore/RoomStateCollaborationData/types'
import { Question } from '../../models/Question'
import type { SlideQuestion } from '../../models/SlideQuestion'
import type { MeetingCubit } from '../MeetingCubit'
import { GroupQuizSlide } from './slides/GroupQuizSlide'

/**
 * The MeetingQuestion a Question that is associated with a Meeting.
 *
 * It's goal is to house all logic required for interacting with a Question in the context of a Meeting.
 *
 */
export class MeetingQuestion extends Question {
  meeting: MeetingCubit
  constructor(meeting: MeetingCubit, model: SlideQuestion) {
    super(model)
    this.meeting = meeting
    makeObservable(this, {
      userAnswer: computed,
      groupAnswer: computed,
      allAnswers: computed,
      allSubmittedAnswers: computed,
      isAnswered: computed,
      isSubmitted: computed,
      collaborationData: computed,
      collaborationDataSorted: computed,
      collaborationDataForCurrentUser: computed,
      collaborationDataForResult: computed,
      sortableCollaborationDataForCurrentUser: computed,
      groupInputTimerStartedCollaborationDataId: computed,
      groupInputStartedCollaborationData: computed,
      groupInputTimeSinceTimerStarted: computed,
      collaborationDataForForm: computed,
    })
  }

  get collaborationData() {
    return this.meeting.collaborationData.models.filter(
      (data) => data.data.questionId === this.id
    )
  }

  get collaborationDataForForm() {
    return this.collaborationData.filter(
      (data) =>
        data.data.collaborationDataType ===
          CollaborationDataType.formElementPartial ||
        data.data.collaborationDataType ===
          CollaborationDataType.aiAgentResponse
    )
  }

  get collaborationDataForSortable() {
    return this.collaborationData.filter(
      (data) =>
        data.data.collaborationDataType === CollaborationDataType.sortableInput
    )
  }

  get groupInputTimerStartedCollaborationDataId() {
    return `${this.id}-timer-start`
  }

  get soloInputTimerStartedCollaborationDataId() {
    const userId = this.meeting.currentUser.uid
    return `${this.id}-timer-start-${userId}`
  }

  get soloInputStartedCollaborationData() {
    return this.meeting.collaborationData.models.find(
      (data) =>
        data.id === this.soloInputTimerStartedCollaborationDataId &&
        data.data.questionId === this.id &&
        data.data.userId === this.meeting.currentUser.uid &&
        data.data.collaborationDataType ===
          CollaborationDataType.sortableTimerStart
    )
  }

  get groupInputStartedCollaborationData() {
    return this.meeting.collaborationData.models.find(
      (data) =>
        data.id === this.groupInputTimerStartedCollaborationDataId &&
        data.data.questionId === this.id &&
        data.data.collaborationDataType ===
          CollaborationDataType.sortableTimerStart
    )
  }

  get groupInputTimeSinceTimerStarted() {
    if (this.meeting.collaborationData.isLoading) return undefined
    const collab = this.groupInputStartedCollaborationData
    if (!collab) return null

    const startedAt = collab.data.updatedAt
    if (!startedAt) return null

    const now = new Date()
    const timeDiff = (now.getTime() - startedAt.getTime()) / 1000 // in seconds

    return Math.round(timeDiff)
  }

  get soloInputTimeSinceTimerStarted() {
    if (this.meeting.collaborationData.isLoading) return undefined

    const collab = this.soloInputStartedCollaborationData
    if (!collab) return null

    const startedAt = collab.data.updatedAt
    if (!startedAt) return null

    const now = new Date()
    const timeDiff = (now.getTime() - startedAt.getTime()) / 1000 // in seconds

    return Math.round(timeDiff)
  }

  startGroupInputSortableTimer() {
    const userId = this.meeting.currentUser.uid

    const data = {
      collaborationDataType: CollaborationDataType.sortableTimerStart,
      userId: userId,
      questionId: this.id,
    }

    return upsertRoomStateCollaborationData(this.meeting.repository, {
      id: this.groupInputTimerStartedCollaborationDataId,
      roomId: this.meeting.roomId,
      data,
    })
  }

  markCollaborationDataReviewed(id: string) {
    return upsertRoomStateCollaborationData(this.meeting.repository, {
      id,
      roomId: this.meeting.roomId,
      data: {
        collaborationDataType: CollaborationDataType.formElementPartial,
        questionId: this.id,
        reviewed: true,
      },
    })
  }

  startSoloInputSortableTimer() {
    const userId = this.meeting.currentUser.uid

    const data = {
      collaborationDataType: CollaborationDataType.sortableTimerStart,
      userId: userId,
      questionId: this.id,
    }

    return upsertRoomStateCollaborationData(this.meeting.repository, {
      id: this.soloInputTimerStartedCollaborationDataId,
      roomId: this.meeting.roomId,
      data,
    })
  }

  clearSoloInputSortableTimer() {
    return deleteRoomStateCollaborationData(this.meeting.repository, {
      id: this.soloInputTimerStartedCollaborationDataId,
      roomId: this.meeting.roomId,
    })
  }

  clearGroupInputSortableTimer() {
    return deleteRoomStateCollaborationData(this.meeting.repository, {
      id: this.groupInputTimerStartedCollaborationDataId,
      roomId: this.meeting.roomId,
    })
  }

  get collaborationDataSorted() {
    return this.collaborationDataForSortable.sort((a, b) => {
      const orderA = a.data.location?.order
      const orderB = b.data.location?.order
      if (orderA === undefined || orderB === undefined) {
        return 0
      }
      return orderA > orderB ? 1 : orderA < orderB ? -1 : 0
    })
  }

  get collaborationDataForCurrentUser() {
    return this.collaborationDataForSortable.filter(
      (data) => data.data.userId === this.meeting.currentUser.uid
    )
  }

  getLowestOrderForCollaborationData() {
    const orders = this.collaborationDataForCurrentUser.map(
      (data) => data.data.location?.order || 0
    )
    const lowestOrder =
      orders.length === 0
        ? 0
        : this.collaborationDataForCurrentUser.reduce(
            (min, data) => Math.min(min, data.data.location?.order || 0),
            Number.MAX_SAFE_INTEGER
          )
    return lowestOrder
  }

  getLowestOrderForCollaborationDataQuestion(questionId: string) {
    const orders = this.collaborationDataForSortable
      .filter((data) => data.data.questionId === questionId)
      .map((data) => data.data.location?.order || 0)
    const lowestOrder =
      orders.length === 0
        ? 0
        : orders.reduce(
            (min, order) => Math.min(min, order),
            Number.MAX_SAFE_INTEGER
          )
    return lowestOrder
  }

  get sortableCollaborationDataForCurrentUser() {
    const figJamData = this.collaborationDataForCurrentUser.filter(
      (data) =>
        data.data.collaborationDataType === CollaborationDataType.sortableInput
    )
    const sortedEntries = figJamData.sort((a, b) => {
      const orderA = a.data.location?.order
      const orderB = b.data.location?.order
      if (orderA === undefined || orderB === undefined) {
        return 0
      }
      return orderA > orderB ? 1 : orderA < orderB ? -1 : 0
    })
    return sortedEntries
  }

  get slideQuestion() {
    return this.model
  }

  get userAnswer() {
    const user = this.meeting.currentUser
    const collection = this.meeting.roomStateAnswersPerUser.get(user.uid)
    if (!collection) return undefined
    return collection.models.find((ua) => ua.data.slideQuestionId === this.id)
  }

  get groupAnswer() {
    return this.meeting.roomStateAnswersForGroup.models.find(
      (ua) => ua.data.slideQuestionId === this.id
    )
  }

  get isAnswered() {
    if (this.meeting.currentSlide instanceof GroupQuizSlide) {
      return !!this.groupAnswer
    }
    return !!this.userAnswer
  }

  get isSubmitted() {
    if (this.meeting.currentSlide instanceof GroupQuizSlide) {
      return !!this.groupAnswer?.data.submitted
    }
    return !!this.userAnswer?.data.submitted
  }

  get allAnswers() {
    return this.meeting.questionAnswers.get(this.id) || []
  }

  get allSubmittedAnswers() {
    return this.allAnswers.filter((a) => a.data.submitted)
  }

  answer(answer: number, answerList?: number[]) {
    return this.sendUserAnswer({
      answer,
      answerList,
      isGroupAnswer: false,
    })
  }

  answerForGroup(answer: number, answerList?: number[]) {
    return this.sendUserAnswer({
      answer,
      answerList,
      isGroupAnswer: true,
    })
  }

  protected sendUserAnswer({
    answer,
    answerList,
    isGroupAnswer,
  }: {
    isGroupAnswer: boolean
    answer: number
    answerList?: number[]
  }) {
    const meeting = this.meeting

    if (!meeting.roomId) return Promise.resolve()

    const answerId = isGroupAnswer ? this.groupAnswer?.id : this.userAnswer?.id

    meeting.logEvent('meeting_answer_question', {
      questionId: this.id,
      answer,
      isGroupAnswer,
    })

    return upsertUserAnswer(meeting.repository, this.id, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      assignmentId: meeting.assignmentId || undefined,
      sectionId: meeting.sectionId || undefined,
      answerId: answerId,
      answer,
      answerList,
      isGroupAnswer,
    })
  }

  async upsertAnswer({
    isGroupAnswer,
    answer,
    answerList,
    submitted,
    answerString,
  }: {
    submitted?: boolean
    isGroupAnswer: boolean
    answer: number
    answerList?: number[]
    answerString?: string
  }) {
    const answerModel = isGroupAnswer ? this.groupAnswer : this.userAnswer
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    await upsertUserAnswer(meeting.repository, this.id, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      assignmentId: meeting.assignmentId || undefined,
      sectionId: meeting.sectionId || undefined,
      answerId: answerModel?.id,
      answer,
      answerList,
      answerString,
      isGroupAnswer: isGroupAnswer,
      submitted: submitted,
    })
  }

  async upsertAnswerObject({
    isGroupAnswer,
    objects,
    submitted,
  }: {
    submitted?: boolean
    isGroupAnswer: boolean
    objects: {
      id: string
      text: string
      userId?: string
    }[]
  }) {
    const answer = isGroupAnswer ? this.groupAnswer : this.userAnswer
    const answerObjects = objects.reduce(
      (acc, obj, index) => ({
        ...acc,
        [obj.id]: {
          id: obj.id,
          text: obj.text,
          userId: obj.userId,
          order: index,
        },
      }),
      {}
    )

    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    await upsertUserAnswer(meeting.repository, this.id, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: answer?.id,
      answer: 0,
      isGroupAnswer: isGroupAnswer,
      answerObjects: answerObjects,
      submitted: submitted,
    })
  }

  async submitUserAnswerObject(
    objects: {
      id: string
      text: string
    }[]
  ) {
    return this.upsertAnswerObject({
      isGroupAnswer: false,
      submitted: true,
      objects,
    })
  }

  async submitGroupAnswerObject(
    objects: {
      id: string
      text: string
    }[]
  ) {
    return this.upsertAnswerObject({
      isGroupAnswer: true,
      submitted: true,
      objects,
    })
  }

  reorderUserAnswerObject(
    list: {
      id: string
      order: number
    }[]
  ) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateAnswerObjectsOrder(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: false,
      answerObjects: list,
    })
  }

  removeUserAnswerObject(id: string) {
    const answerObjects = this.userAnswer?.data.answerObjects || {}

    if (!answerObjects[id]) return Promise.resolve()

    const cloned = { ...answerObjects }

    delete cloned[id]

    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return setAnswerObjects(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: false,
      answerObjects: cloned,
    })
  }

  updateGroupAnswerObject({
    id,
    text,
    order,
  }: {
    id: string
    text: string
    order: number
  }) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateAnswerObjects(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: true,
      answerObjects: [
        {
          id,
          text,
          order,
        },
      ],
    })
  }

  reorderGroupAnswerObject(
    list: {
      id: string
      order: number
      text: string
      userId: string
    }[]
  ) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateAnswerObjectsOrder(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: true,
      answerObjects: list.map((item) => ({
        id: item.id,
        order: item.order,
        text: item.text,
        userId: item.userId,
      })),
    })
  }

  removeUserAnswer(answerId: string) {
    const meeting = this.meeting
    if (!meeting.roomId) return
    return removeUserAnswer(meeting.firestore, {
      roomId: meeting.roomId,
      userId: meeting.currentUser.uid,
      answerId,
      isGroupAnswer: !!this.groupAnswer,
    })
  }

  submitAnswer() {
    this.meeting.logEvent('meeting_submit_question_answer', {
      questionId: this.id,
    })
    this.submitUserAnswer({ isGroupAnswer: false })
  }

  submitGroupAnswer() {
    this.meeting.logEvent('meeting_submit_question_group_answer', {
      questionId: this.id,
    })
    this.submitUserAnswer({ isGroupAnswer: true })
  }

  protected async submitUserAnswer({
    isGroupAnswer,
  }: {
    isGroupAnswer: boolean
  }) {
    const meeting = this.meeting
    if (!meeting.roomId) return

    const answerId = isGroupAnswer ? this.groupAnswer?.id : this.userAnswer?.id

    if (!answerId) return

    submitUserAnswer(meeting.firestore, {
      roomId: meeting.roomId,
      userId: meeting.currentUser.uid,
      answerId,
      isGroupAnswer,
    })
  }

  upsertCollaborationRawData(
    id: string,
    data: {
      type: CollaborationDataType
      data?: Record<string, unknown>
      reviewed?: boolean
    },
    options: {
      merge?: boolean
    } = { merge: false }
  ) {
    return upsertRoomStateCollaborationData(this.meeting.repository, {
      id,
      roomId: this.meeting.roomId,
      data: {
        ...data,
        questionId: this.id,
        collaborationDataType: data.type,
      },
      merge: options.merge ?? false,
    })
  }

  upsertCollaborationData(
    id: string,
    data: {
      type: CollaborationDataType
      label: string
      location: {
        x: number
        y: number
        container: string
        order?: number
      }
      sourceId?: string
      userId: string
    }
  ) {
    return upsertRoomStateCollaborationData(this.meeting.repository, {
      id,
      roomId: this.meeting.roomId,
      data: {
        ...data,
        questionId: this.id,
        collaborationDataType: data.type,
      },
    })
  }

  upsertMultipleCollaborationDatas(
    datas: {
      id: string
      data: {
        type: CollaborationDataType
        label: string
        location: {
          x: number
          y: number
          container: string
          order?: number
        }
        sourceId?: string
        userId: string
      }
    }[]
  ) {
    return upsertMultipleRoomStateCollaborationDatas(this.meeting.repository, {
      roomId: this.meeting.roomId,
      datas: datas.map((item) => ({
        id: item.id,
        data: {
          ...item.data,
          questionId: this.id,
          collaborationDataType: item.data.type,
        },
      })),
    })
  }

  removeCollaborationData(id: string) {
    return deleteRoomStateCollaborationData(this.meeting.repository, {
      id,
      roomId: this.meeting.roomId,
    })
  }

  reorderCollaborationData(
    list: {
      id: string
      order: number
      container: string
      x?: number
      y?: number
    }[]
  ) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateRoomStateCollaborationDataOrder(meeting.repository, {
      questionId: this.id,
      roomId: meeting.roomId,
      list: list,
    })
  }

  saveCollaborationDataAsUserAnswer({
    submitted = true,
  }: {
    submitted: boolean
  }) {
    const answerObject = this.sortableCollaborationDataForCurrentUser.map(
      (data, index) => ({
        id: data.id,
        text: data.data.label || '',
        order: index,
      })
    )

    return this.upsertAnswerObject({
      isGroupAnswer: false,
      submitted: submitted,
      objects: answerObject,
    })
  }

  get collaborationDataForResult() {
    return this.collaborationData.filter(
      (data) => data.data.location?.container === 'result'
    )
  }

  saveResultAsGroupAnswer({ submitted = true }: { submitted: boolean }) {
    const collabData = this.collaborationData
    const resultDatas = collabData
      .filter((data) => data.data.location?.container === 'result')
      .sort((a, b) => {
        const orderA = a.data.location?.order
        const orderB = b.data.location?.order
        if (orderA === undefined || orderB === undefined) {
          return 0
        }
        return orderA > orderB ? 1 : orderA < orderB ? -1 : 0
      })

    const result = resultDatas.map((data, index) => ({
      id: data.id,
      text: data.data.label || '',
      userId: data.data.userId || '',
      order: index,
    }))

    return this.upsertAnswerObject({
      isGroupAnswer: true,
      submitted: submitted,
      objects: result,
    })
  }

  resetCollaborationData() {
    const promises: Promise<void>[] = []
    for (const data of this.meeting.collaborationData.models) {
      if (data.data.questionId === this.id) {
        const promise = deleteRoomStateCollaborationData(
          this.meeting.repository,
          {
            id: data.id,
            roomId: this.meeting.roomId,
          }
        )
        promises.push(promise)
      }
    }
    return Promise.all(promises)
  }
}
